import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Box, Checkbox, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Button, DateTimeInput, Filter, NumberInput, ReferenceInput, SelectInput } from 'ra-ui-materialui';
import React, { useCallback, useEffect, useState } from 'react';
import { useListContext, useRecordContext } from 'react-admin';
import { LazyLoadAutoCompleteInput, List, ListPagination, Permission, UploadInvoiceXml } from '../../components';
import { PERMISSIONS } from '../../constants';
import { FillinReviewStatus, FillinStatus } from '../../models';
import { UserRole } from '../../providers/authProvider';
import { getChainId } from '../../utils/utils';
import { FillinDatagrid, ScreenType } from '../Fillin';

const CheckBoxRow: React.FC<any> = ({ selectedIds, currentSelectedIds, onChange }) => {
  const record = useRecordContext();

  const onChangeHandler = (e: any) => {
    e.stopPropagation();
    onChange(record);
  };

  const activeCheckBox = useCallback(() => {
    return !!(selectedIds.includes(record.id) || currentSelectedIds.includes(record.id));
  }, [selectedIds, currentSelectedIds]);

  return (
    <Checkbox
      checked={activeCheckBox()}
      onChange={onChangeHandler}
      onClick={(e) => {
        e.stopPropagation();
      }}
      disabled={(currentSelectedIds !== record.id && selectedIds.includes(record.id)) || (currentSelectedIds && selectedIds.includes(currentSelectedIds))}
    />
  );
};


const FillinList = (props: any) => {
  return (
    <FillinDatagrid
      rowClick={null}
      screen={ScreenType.billShow}
      bulkActions={(
        <CheckBoxRow selectedIds={props.selectedIds} currentSelectedIds={props.currentSelectedIds} onChange={props.handleRadioClick} />
      )}
    />
  );
}

const ListFilters = ({ xmlLoaded, ...props }: any) => {
  const { filterValues, filter, setFilters, } = useListContext();

  useEffect(() => {
    setFilters({
      ...filter,
      ...filterValues,
      ...(xmlLoaded.fuel ? {
        fuelId: xmlLoaded.fuel.id,
      } : {}),
      ...(xmlLoaded.amount ? {
        estimateAmount: xmlLoaded.amount,
      } : {}),
      ...(xmlLoaded.value ? {
        estimateValue: xmlLoaded.value,
      } : {}),
    }, {}, true);
  }, [xmlLoaded]);

  const clearFilters = useCallback(() => {
    setFilters({
      ...filter,
      from: dayjs().subtract(1, 'month').startOf('day').toISOString(),
      to: dayjs().endOf('day').toISOString()
    }, {}, true);
  }, [filter, filterValues]);

  return (
    <Filter {...props}>
      <DateTimeInput
        source="from"
        label="De"
        sx={{ marginBottom: 0.5 }}
        alwaysOn
      />
      <DateTimeInput
        source="to"
        label="Até"
        sx={{ marginBottom: 0.5 }}
        alwaysOn
      />
      {
        Permission({
          userRole: UserRole.admin,
          children: (
            <ReferenceInput
              source="chainId"
              reference="chains"
              alwaysOn
            >
              <SelectInput label="Rede" optionText="name" emptyText="Todos" />
            </ReferenceInput>
          )
        })
      }
      <NumberInput
        sx={{ maxWidth: 110 }}
        source="estimateAmount"
        label="Litros"
        InputProps={{
          disabled: true
        }}
        alwaysOn
      />
      <NumberInput
        sx={{ maxWidth: 110 }}
        source="estimateValue"
        label="Valor"
        InputProps={{
          disabled: true
        }}
        alwaysOn
      />
      <ReferenceInput
        reference="stations"
        source="stationId"
        sort={{ field: 'name', order: 'ASC' }}
        //@ts-ignore
        perPage={false}
        filter={filterValues?.chainId ? { chainId: filterValues.chainId } : getChainId() ? { chainId: getChainId() } : {}}
        alwaysOn
      >
        <SelectInput optionText="name" label="Posto" emptyText="Todos" />
      </ReferenceInput>
      <LazyLoadAutoCompleteInput
        label="Placa do Veículo"
        source="licensePlate"
        format={choice => choice.licensePlate}
        style={{ minWidth: 220 }}
        reference="vehicles"
        alwaysOn
      />
      <ReferenceInput
        reference="fuels"
        source="fuelId"
        sort={{ field: 'name', order: 'ASC' }}
        //@ts-ignore
        perPage={false}
        alwaysOn
      >
        <SelectInput optionText="name" label="Combustível" emptyText="Todos" />
      </ReferenceInput>
      <ReferenceInput
        reference="sub-companies"
        source="subCompanyId"
        sort={{ field: 'name', order: 'ASC' }}
        //@ts-ignore
        perPage={false}
        alwaysOn
      >
        <SelectInput optionText="name" label="Transportadora" emptyText="Todas" />
      </ReferenceInput>
      <Button
        startIcon={<FilterAltOffIcon />}
        variant="outlined"
        size="large"
        label="Limpar"
        onClick={clearFilters}
        sx={{ marginBottom: 0.8 }}
        //@ts-ignore
        alwaysOn
      />
    </Filter>
  );
}

const InvoiceUpload: React.FC = (props) => {

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedFillins, setSelectedFillins] = useState<{ id: string }[]>([]);
  const [currentSelectedIds, setCurrentSelectedIds] = useState<string[]>([]);
  const [xmlLoaded, setXmlLoaded] = useState<any>({});

  useEffect(() => {
    setSelectedFillins((fillins: any[]) => fillins.filter((fillin) => [...selectedIds, ...currentSelectedIds].includes(fillin.id)));
  }, [selectedIds, currentSelectedIds]);

  const initialFilters: any = {
    status: FillinStatus.approved,
    stationReviewStatus: FillinReviewStatus.approved,
    generateFinancialMovement: true,
    hasInvoice: false,
  }

  if (getChainId()) {
    initialFilters.chainId = getChainId();
  }

  const resetInitialValues = (hardReset?: boolean) => {
    setCurrentSelectedIds([]);
    if (hardReset) {
      setSelectedFillins([]);
      setSelectedIds([]);
      setXmlLoaded({});
    }
  }

  const handleRadioClick = useCallback((record: { id: string }) => {
    if (currentSelectedIds.includes(record.id)) {
      setCurrentSelectedIds([...currentSelectedIds.filter(id => id !== record.id)]);
      setSelectedFillins(fillins => ([...selectedFillins.filter(f => f.id !== record.id)]));
    } else {
      setSelectedFillins(fillins => ([...selectedFillins, record]));
      setCurrentSelectedIds([...currentSelectedIds, record.id]);
    }
  }, [currentSelectedIds, selectedFillins]);


  return (
    <Permission permission={PERMISSIONS.CREATE_INVOICE}>
      <UploadInvoiceXml
        selectedIds={selectedIds}
        selectedFillins={selectedFillins}
        setSelectedIds={setSelectedIds}
        currentSelectedIds={currentSelectedIds}
        setCurrentSelectedIds={setCurrentSelectedIds}
        xmlLoaded={xmlLoaded}
        setXmlLoaded={setXmlLoaded}
        resetInitialValues={resetInitialValues}
        totalValue={4}
      >
        {
          (xmlLoaded && xmlLoaded.destCnpj) ?
            <List
              {...props}
              resource="fillins"
              perPage={25}
              pagination={<ListPagination />}
              exporter={false}
              title="Importar nota fiscal"
              filter={getChainId() ? { chainId: getChainId(), ...initialFilters } : initialFilters}
              filterDefaultValues={{
                ...(xmlLoaded.issueDate ? {
                  from: dayjs(xmlLoaded.issueDate).subtract(1, 'day').startOf('day').toISOString(),
                  to: dayjs(xmlLoaded.issueDate).endOf('day').toISOString(),
                } : {
                  from: dayjs().subtract(1, 'month').startOf('day').toISOString(),
                  to: dayjs().endOf('day').toISOString(),
                }),
                ...(xmlLoaded.fuel ? {
                  fuelId: xmlLoaded.fuel.id,
                } : {}),
                ...(xmlLoaded.station ? {
                  stationId: xmlLoaded.station.id,
                } : {}),
                ...(xmlLoaded.vehicleBase ? {
                  subCompanyId: xmlLoaded.vehicleBase.subCompanyId,
                } : {}),
                ...(xmlLoaded.amount ? {
                  estimateAmount: xmlLoaded.amount,
                } : {}),
                ...(xmlLoaded.value ? {
                  estimateValue: xmlLoaded.value,
                } : {}),
              }}
              filters={<ListFilters {...props} xmlLoaded={xmlLoaded} />}
              sort={{ field: 'receivedAt', order: 'DESC' }}
              hasCreate={false}
              storeKey={false}
            >
              <FillinList
                selectedIds={selectedIds}
                currentSelectedIds={currentSelectedIds}
                setCurrentSelectedIds={setCurrentSelectedIds}
                handleRadioClick={handleRadioClick}
              />
            </List>
            :
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography variant="subtitle1" align="center" gutterBottom>Informe o arquivo XML para listar os abastecimentos</Typography>
            </Box>
        }
      </UploadInvoiceXml>
    </Permission>
  );
}

export default InvoiceUpload;